/**
 * Abstract painting ideas — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_painting_ideas`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const abstractPaintingIdeasPack: ToilePack = {
    slug: 'abstract-painting-ideas',
    category: 'craft',
    name: 'Abstract painting ideas',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'pour', label: 'Pour painting', subject: 'a canvas of a pour painting with teal, white and gold cells spreading across it'},
        {key: 'blocks', label: 'Earth blocks', subject: 'a canvas of soft blocks of terracotta, sand and sage divided by one thin gold line'},
        {key: 'knife', label: 'Knife strokes', subject: 'a canvas of thick palette-knife strokes in deep blues and a slash of white'},
    ],
    styles: [
        {key: 'sofa', label: 'Above a sofa', style: 'hung on a white living-room wall above a linen sofa with two cushions', palette: 'Warm whites and natural linen in the room, the canvas the one strong colour.', framing: 'Seen at eye level from across the room, the canvas centred above the sofa.'},
        {key: 'easel', label: 'On the easel', style: 'standing on a tall wooden easel, a palette knife and a cup of pouring medium on its ledge', palette: "Pale wood and drips of the canvas's own colours on the easel.", framing: 'Seen straight on at eye level, the canvas filling the middle of the frame.'},
        {key: 'floor', label: 'Leaning', style: 'leaning against a pale wall on a light oak floor beside a tall potted olive tree', palette: 'Soft white and green with pale oak, calm around the painting.', framing: 'Seen at eye level from a step back, the canvas centred with the plant to one side.'},
    ],
    directions: ['black and gold', 'pastel', 'huge canvas'],
    directionHint: 'try: black and gold',
    seo: {
        title: 'Abstract Painting Ideas: Pour, Blocks, Knife Strokes',
        metaDescription: 'Nine abstract canvas ideas for a wall that needs color: a teal pour, earth-tone blocks and deep blue knife strokes, shown three ways.',
        h1: 'Abstract painting ideas: pour painting, earth blocks, knife strokes',
        tagline: 'Nine finished abstract canvases, three textures, each shown above a sofa, on an easel and leaning against a wall.',
        intro: 'Abstract canvases are painted for a wall that needs color and texture rather than a scene. These nine show three: a teal, white and gold pour painting with spreading cells, soft blocks of terracotta, sand and sage divided by a thin gold line, and thick palette-knife strokes in deep blue with a slash of white. Type a different palette or mood, such as black and gold or pastel, and the same textures carry it.',
        useCases: [
            'a large piece above a sofa',
            'a bold accent in a hallway',
            'a gift for a new home',
            'a first textured canvas',
        ],
        faq: [
            {q: 'What is a pour painting?', a: 'Paint poured onto the canvas so the colors spread into cells and marbled shapes. The picture shows the finished result.'},
            {q: 'Do the nine pictures show any figures or text?', a: 'No. All nine are abstract, with no people, objects or lettering.'},
            {q: 'Can I change the palette?', a: 'Yes. Type a different palette or mood and the three textures carry it.'},
            {q: 'Where are the canvases shown?', a: 'Hung above a linen sofa, standing on a tall wooden easel, or leaning against a pale wall beside a potted olive tree.'},
        ],
    },
}
